import { ContentSeries } from '@/client/courses';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import classNames from 'classnames';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledSchedulingBand = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
  width: 100%;
  box-sizing: border-box;
  padding: var(--default-padding-small) var(--default-padding);
  background-color: var(--white-main);
  border-bottom: 1px solid var(--gray-dark);

  &.master-off {
    border-top: 1px solid var(--gray-dark);
  }

  .switch-container {
    display: flex;
  }

  &.read-only {
    .p-inputswitch {
      opacity: 1;
      cursor: default;
    }

    label {
      cursor: default;
    }
  }
`;

const StyledMasterRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
`;

const StyledMasterLabel = styled.label`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 600;
  color: var(--black-main);
  cursor: pointer;

  i {
    font-size: var(--medium-font-size);
  }
`;

const StyledFoldToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: var(--xxsmall-padding);
  border: none;
  background: none;
  color: var(--black-main);
  font-size: var(--medium-font-size);
  cursor: pointer;
`;

const StyledSeriesRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--small-padding) var(--medium-big-padding);
`;

const StyledSeriesItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
`;

const StyledSeriesLabel = styled.label`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 600;
  color: var(--black-main);
  cursor: pointer;

  i {
    font-size: var(--medium-font-size);
  }
`;

type AutomaticSchedulingTogglesProps = {
  autoEnroll: boolean;
  subscribedSeries: ContentSeries[];
  onAutoEnrollToggle?: (nextValue: boolean) => void;
  onSeriesToggle?: (series: ContentSeries, nextValue: boolean) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  disabled?: boolean;
  readOnly?: boolean;
};

export const AutomaticSchedulingToggles: React.FC<
  AutomaticSchedulingTogglesProps
> = ({
  autoEnroll,
  subscribedSeries,
  onAutoEnrollToggle,
  onSeriesToggle,
  isExpanded,
  onToggleExpanded,
  disabled = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const instanceId = useId().replace(/:/g, '');

  const masterSwitchId = `automatic-scheduling-switch-${instanceId}`;
  const seriesRowId = `automatic-scheduling-series-${instanceId}`;
  const isInteractionDisabled = disabled || readOnly;

  return (
    <StyledSchedulingBand
      className={classNames({
        'master-off': !autoEnroll,
        'read-only': readOnly,
      })}
      data-testid="automatic-scheduling-toggles"
    >
      <StyledMasterRow>
        <StyledMasterLabel htmlFor={masterSwitchId}>
          {t('planner.automaticScheduling')}
        </StyledMasterLabel>
        <AppIconTooltip
          target={`automatic-scheduling-info-${instanceId}`}
          content={t('planner.automaticScheduling.tooltip')}
        />
        <AppSwitch
          className="v2 big"
          inputId={masterSwitchId}
          checked={autoEnroll}
          disabled={isInteractionDisabled}
          onChange={(e) => onAutoEnrollToggle?.(!!e.value)}
        />
        {autoEnroll && (
          <StyledFoldToggle
            type="button"
            aria-expanded={isExpanded}
            aria-controls={seriesRowId}
            aria-label={
              isExpanded ? t('generic.collapse') : t('generic.expand')
            }
            onClick={onToggleExpanded}
          >
            <i
              className={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
            />
          </StyledFoldToggle>
        )}
      </StyledMasterRow>
      {autoEnroll && isExpanded && (
        <StyledSeriesRow id={seriesRowId}>
          {Object.values(ContentSeries).map((series) => {
            const seriesSwitchId = `series-switch-${series}-${instanceId}`;

            return (
              <StyledSeriesItem key={series}>
                <StyledSeriesLabel htmlFor={seriesSwitchId}>
                  {t(`content.series.${series}`)}
                </StyledSeriesLabel>
                <AppIconTooltip
                  target={`series-info-${series}-${instanceId}`}
                  content={t(`planner.series.${series}.tooltip`)}
                />
                <AppSwitch
                  className="v2 small"
                  inputId={seriesSwitchId}
                  checked={subscribedSeries.includes(series)}
                  disabled={isInteractionDisabled}
                  onChange={(e) => onSeriesToggle?.(series, !!e.value)}
                />
              </StyledSeriesItem>
            );
          })}
        </StyledSeriesRow>
      )}
    </StyledSchedulingBand>
  );
};
